import { HttpClient } from '@angular/common/http';
import { ConfigInstanceToken, ConfigInterface } from '../../interfaces/config.interface';
import { Inject, Injectable } from '@angular/core';
import { LogInstanceToken, LogInterface, PaceInsightsService } from '@evorait/pace-logging';
import { Observable, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { ENDPOINTS } from '../../enums/endpoints.enum';
import { OdataResponse, ResponseWithResults } from '../../interfaces/online-only.interface';
import { CategoryModel } from '../../models/complex/internal-info/category.model';
import { InternalInfoModel } from '../../models/complex/internal-info/internal-info.model';
import { NotificationIdentifierModel } from '../../models/low-level/notification-identifier.model';
import { NotificationModel } from '../../models/low-level/notification.model';
import { ODataNotification, ODataNotificationKeys } from '../../state/odata-types/notification.type';
import { OdataService } from '../../services/odata/odata.service';
import { getUuidV4 } from '../../utilities/generic-utilities';
import { isDefined } from '../../utilities/is-defined';
import { ODataUtilities } from '../../utilities/odata-utilities';
import { UriUtilitiesService } from '../uri-utilities/uri-utilities.service';
import { UserMasterInstanceToken, UserMasterInterface } from '../../interfaces/user-master.interface';
import { NotificationWithRaw, OdataNotificationServiceInterface } from './odata-notification.interface';
import {
    NotificationHistoryModel,
    ODataNotificationHistoryModel,
} from '../../models/low-level/notification-history.model';
import { EXPANDS } from '../../enums/expands.enum';
import { CACHE_FIRST_HEADER } from '@evorait/pace-offline';

const NOTIFICATION_LOG_FIELDS: ODataNotificationKeys[] = [
    'NotificationNo',
    'TaskNumber',
    'Status',
    'UserStatus',
    'NotificationType',
    'EquipmentCount',
    'FunctionalLoc',
    'Plant',
    'SalesOrg',
    'Effect',
    'Country',
    'NdpVersion',
    'Notification',
];

@Injectable({
    providedIn: 'root',
})
export class OdataNotificationService implements OdataNotificationServiceInterface {
    public incorrectNotificationsList: string[] = [];
    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly uriUtilities: UriUtilitiesService,
        private readonly http: HttpClient,
        @Inject(UserMasterInstanceToken) private readonly userMasterService: UserMasterInterface,
        private readonly odataService: OdataService,
        @Inject(ConfigInstanceToken) private readonly configService: ConfigInterface,
        private readonly paceInsightsService: PaceInsightsService,
    ) {}

    public getNotifications(cacheFirst: boolean): Observable<NotificationModel[]> {
        return this.getNotificationsWithRaw(cacheFirst).pipe(map((pairs) => pairs.map((pair) => pair.model)));
    }

    public getNotificationsWithRaw(cacheFirst: boolean): Observable<NotificationWithRaw[]> {
        this.incorrectNotificationsList = [];

        // During debugging it turned out that this is actually the first place PACE loads notifications.
        // So the first dependency in PACE on config parameters to control the data coming from NdPHeaderSet came
        // in with 5.21.0. InstallationPaperwork and SourceReplacement came in, which had an effect on the $expand
        // definition. There have been entities in the query which would be valid in 6 month on PROD.
        // From here on every requirement needs to be checked if its requesting whole entities in the NdPHeaderSet
        // $expand which will be transported in parallel and also add functionality to dynamically provide the $expand
        // definition on call.
        let query = this.userMasterService.setSalesOrgAndPlantForUrl(
            this.uriUtilities.getUriForContent() + ENDPOINTS.NotificationHeaders,
        );
        let expand = this.uriUtilities.getNotificationHeaderByAppId();

        const paperworkEnabled = this.configService.getEnableInstallationPaperwork();
        if (paperworkEnabled) {
            expand = `${expand},${EXPANDS.ExpandEntityPaperwork}`;
        }

        const sourceReplacementEnabled = this.configService.isSourceReplacementEnabled();
        if (sourceReplacementEnabled) {
            expand = `${expand},${EXPANDS.ExpandEntitySourceReplacement}`;
        }

        query = this.uriUtilities.appendParameter(query, expand);

        const headers: { [key: string]: string } = {};
        if (cacheFirst) {
            headers[CACHE_FIRST_HEADER] = 'true';
        }

        return this.http.get<OdataResponse<ODataNotification>>(query, { headers }).pipe(
            tap((response) => {
                const data = response?.d;

                const notifications = Array.isArray((data as ResponseWithResults<ODataNotification>)?.results)
                    ? (data as ResponseWithResults<ODataNotification>).results
                    : [];

                this.paceInsightsService.trackTrace(
                    { message: 'NdpHeaderSet fetch succeeded' },
                    {
                        query,
                        numNotifications: notifications.length,
                        notifications: notifications.map((n) =>
                            Object.fromEntries(NOTIFICATION_LOG_FIELDS.map((key) => [key, n[key]])),
                        ),
                    },
                );
            }),
            map((response) =>
                ODataUtilities.transformResponseDataToArray(response.d)
                    .map((n) => {
                        try {
                            return { model: new NotificationModel(n), raw: n };
                        } catch (e) {
                            this.log.error(
                                `OdataNotificationService: getNotifications : new NotificationModel failed for ${
                                    n.NotificationNo
                                }:${n.TaskNumber} due to ${JSON.stringify(e)}`,
                            );
                            this.incorrectNotificationsList.push(`${n.NotificationNo}:${n.TaskNumber}`);
                        }
                    })
                    .filter(isDefined),
            ),
            catchError((err) => {
                this.log.error('getNotifications - request error: ' + JSON.stringify(err));
                return throwError(() => err);
            }),
        );
    }

    public getNotificationsHistory(numberOfDays: number): Observable<NotificationHistoryModel[]> {
        let query = this.userMasterService.setSalesOrgAndPlantForUrl(
            this.uriUtilities.getUriForContent() + ENDPOINTS.NotificationHistory,
        );
        const filter = `NumberOfDays eq '${numberOfDays}'`;
        const format = '$format=json';

        query = this.uriUtilities.appendFilterValue(query, filter);
        query = this.uriUtilities.appendParameter(query, format);

        return this.http.get<OdataResponse<ODataNotificationHistoryModel>>(query).pipe(
            map((response) =>
                ODataUtilities.transformResponseDataToArray(response.d)
                    .map((notification) => {
                        try {
                            return new NotificationHistoryModel(notification);
                        } catch (e) {
                            this.log.error(
                                `OdataNotificationService: getNotificationsHistory : new NotificationModel failed for ${
                                    notification.NotificationNo
                                }:${notification.TaskNumber} due to ${JSON.stringify(e)}`,
                            );
                        }
                    })
                    .filter(isDefined),
            ),
            catchError((err) => {
                this.log.error('getNotificationsHistory - request error: ' + JSON.stringify(err));
                return throwError(() => err);
            }),
        );
    }

    public sendInternalInfoModel(
        internalInfoModel: InternalInfoModel | CategoryModel,
        notificationId: NotificationIdentifierModel,
    ): Observable<unknown> {
        return this.odataService.deepInsertRequest(
            internalInfoModel,
            ENDPOINTS.InternalInfoSubmitSet,
            `${notificationId.toString()}-${getUuidV4()}`,
        );
    }
}
